/*** Utility Functions that perform tasks to clean data returned from Core Service API's from the Server. */

import { AdjustmentType } from '@core/enums';
import { Check } from '@core/models';

/**
 ** Convert Object Properties from PascalCase to camelCase.
 * 1. Check if data collection passed in argument is an Object.
 * ___
 * a. Create a new object. Create an enumerable array with recursive data checks. Return new camelized object.
 * ___
 * 2. Check if data collection passed is an Array.
 * ___
 * a. Return a new Array with recursive data checks.
 * ___
 * 3. Return camelized data collection.
 * ___
 */
export const keysToCamelCase = (dataCollection: any): any => {
  const camelize = (str: string) => {
    //* if all uppercase return all lowercase
    if (str === str.toUpperCase()) return str.toLowerCase();

    //* Return PascalCase string into camelized format *(PascalCase => camelCase)
    return str
      .replace(/(?:^\w|\[A-Z\]|\b\w)/g, (word: string, index: number) =>
        index === 0 ? word.toLowerCase() : word.toUpperCase()
      )
      .replace(/\s+/g, '');
  };
  if (dataCollection == null) {
    return dataCollection;
  } else if (Array.isArray(dataCollection)) {
    return dataCollection.map((value: any) => {
      return keysToCamelCase(value);
    });
  } else if (dataCollection instanceof Date) {
    return dataCollection;
  } else if (typeof dataCollection === 'object') {
    const newObject: Record<string, unknown> = {};

    Object.keys(dataCollection).forEach(
      (k: string): any => (newObject[camelize(k)] = keysToCamelCase(dataCollection[k] as any))
    );
    return newObject;
  }

  return dataCollection;
};
/**
 * @param {{ [key: string]: string }} cleanKeys - assigned cleaned properties of data object.
 * @param {any} rawData - raw data object from api.
 */
export const cleanApiKeys = (cleanKeys: { [key: string]: string }, rawData: any) =>
  Object.assign(
    {},
    ...Object.keys(rawData).map(key => {
      const newKey = cleanKeys[key] || key;
      return { [newKey]: rawData[key] };
    })
  );

export const checkDatesDescending = (check1: Check, check2: Check): number => {
  return new Date(check2.checkDate).getTime() - new Date(check1.checkDate).getTime();
};

export const isPastAndNotVoid = (check: Check): boolean => {
  const isPast = new Date(check.checkDate) < new Date();
  return isPast && check.adjustmentType.toUpperCase().trim() !== AdjustmentType.Void;
};

/**
 * Return the arguments as strings, using JSON.stringify if necessary
 * @param args
 */
export function stringify(args: any[]): string[] {
  const strings = args.map(a => {
    let s = a ? a.toString() : a;
    if (s && s.startsWith('[object')) {
      s = JSON.stringify(a);
    }
    return s;
  });
  return strings;
}

/** Interface for an object that can be indexed with a string key */
export interface Indexable<T = any> {
  [key: string]: T;
}
